<template>
  <v-layout>
    <v-dialog :modelValue="dialog" @click:outside="$emit('updateDialog', false)" @keydown.esc="$emit('updateDialog', false)" max-width="750px" transition="dialog-bottom-transition">
      <v-card>
        <v-form @submit.prevent ref="form" v-model="valid" v-on:submit.prevent="onSubmit">
          <v-card-title>
            <span class="headline">Credenciales API{{ item.name ? " - " + item.name : "" }}</span>
          </v-card-title>

          <v-card-text>
            <div v-if="!has_credentials" class="text-center py-4">
              <p class="mb-4">Este cliente todavía no tiene credenciales API generadas.</p>
              <v-btn color="success" :loading="loading" @click="generate">Generar credenciales</v-btn>
            </div>

            <div v-else class="row">
              <div class="col-md-12">
                <a
                  v-if="api_doc_url"
                  class="d-inline-flex align-center px-4"
                  :href="api_doc_url"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <v-icon size="small" class="mr-1">mdi-file-document-outline</v-icon>
                  Ver documentación de la API
                  <v-icon size="x-small" class="ml-1">mdi-open-in-new</v-icon>
                </a>

                <v-divider class="my-4"></v-divider>

                <v-text-field label="URL de la API" :modelValue="api_base_url" readonly class="locked-field" hint="URL base para consumir la API del cliente." persistent-hint>
                  <template v-slot:append>
                    <v-icon v-if="api_base_url" size="small" @click="copy(api_base_url)" title="Copiar">mdi-content-copy</v-icon>
                  </template>
                </v-text-field>

                <v-text-field label="API ID" v-model="item.api_id" readonly class="locked-field" hint="Se genera automáticamente al regenerar las credenciales." persistent-hint>
                  <template v-slot:append>
                    <v-icon v-if="item.api_id" size="small" @click="copy(item.api_id)" title="Copiar">mdi-content-copy</v-icon>
                  </template>
                </v-text-field>

                <v-text-field
                  label="API Secret"
                  v-model="item.api_secret"
                  readonly
                  class="locked-field"
                  :type="show_secret ? 'text' : 'password'"
                  hint="Solo lectura. Para cambiarlo, tildar 'Regenerar credenciales'."
                  persistent-hint
                >
                  <template v-slot:append>
                    <v-icon size="small" class="mr-2" @click="show_secret = !show_secret" :title="show_secret ? 'Ocultar' : 'Mostrar'">{{ show_secret ? "mdi-eye" : "mdi-eye-off" }}</v-icon>
                    <v-icon v-if="item.api_secret" size="small" @click="copy(item.api_secret)" title="Copiar">mdi-content-copy</v-icon>
                  </template>
                </v-text-field>

                <div class="mt-6">
                  <v-btn color="warning" type="button" :loading="loading" @click="generate">Regenerar credenciales</v-btn>
                  <div class="text-caption mt-2">Genera un nuevo API ID y API Secret, sobrescribiendo los actuales.</div>
                </div>

                <v-divider class="my-4"></v-divider>

                <v-text-field label="Webhook URL" v-model="item.webhook_url" clearable :rules="item.webhook_url ? [rules.url] : []" hint="Opcional. Puede editarse o borrarse." persistent-hint></v-text-field>

                <v-text-field
                  v-if="item.webhook_url"
                  label="Webhook Token"
                  v-model="item.webhook_key"
                  clearable
                  :type="show_webhook_key ? 'text' : 'password'"
                  :rules="[rules.webhook_key]"
                  hint="Opcional. Si se configura, se envía en el header indicado abajo."
                  persistent-hint
                >
                  <template v-slot:append>
                    <v-icon size="small" @click="show_webhook_key = !show_webhook_key" :title="show_webhook_key ? 'Ocultar' : 'Mostrar'">{{ show_webhook_key ? "mdi-eye" : "mdi-eye-off" }}</v-icon>
                  </template>
                </v-text-field>

                <div v-if="item.webhook_url" class="row">
                  <div class="col-md-7">
                    <v-text-field
                      label="Nombre del header"
                      v-model="item.webhook_auth_header"
                      placeholder="Authorization"
                      :rules="[rules.header_name]"
                      hint="Header en el que se envía el token (ej. Authorization, Key, X-API-Key)."
                      persistent-hint
                    ></v-text-field>
                  </div>
                  <div class="col-md-5">
                    <v-checkbox label="Anteponer Bearer" v-model="item.webhook_auth_bearer" density="compact" hide-details></v-checkbox>
                  </div>
                </div>

                <div v-if="item.webhook_url || saved_webhook_url" class="d-flex align-center flex-wrap" style="margin-top: 10px; gap: 12px">
                  <v-tooltip v-if="saved_webhook_url" bottom :disabled="!has_unsaved_webhook_changes">
                    <template v-slot:activator="{ props }">
                      <div v-bind="props">
                        <v-btn color="primary" variant="outlined" type="button" :loading="loading" :disabled="has_unsaved_webhook_changes" @click="testWebhook">Probar webhook</v-btn>
                      </div>
                    </template>
                    <span>Guardá los cambios del webhook antes de probarlo.</span>
                  </v-tooltip>
                  <span v-if="item.webhook_url" class="text-caption">
                    Se envía: <code>{{ webhook_auth_preview }}</code>
                  </span>
                </div>

                <template v-if="item.webhook_url">
                  <v-divider class="my-4"></v-divider>

                  <div class="text-subtitle-1">Eventos suscriptos</div>
                  <div class="text-caption mb-2">Los webhooks de los eventos no tildados se registran igual pero no se envían (quedan como "No suscripto").</div>
                  <v-checkbox
                    v-for="event_type in event_types"
                    :key="event_type.id"
                    v-model="item.webhook_events"
                    :value="event_type.id"
                    :label="event_type.name"
                    density="compact"
                    hide-details
                  ></v-checkbox>
                </template>
              </div>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" text @click="$emit('updateDialog', false)">Cerrar</v-btn>
            <v-btn v-if="has_credentials" color="success" text type="submit" :loading="loading">Guardar</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import ValidMixin from "@/mixins/ValidMixin.vue";
import { generalService } from "@/libs/ws/generalService";
import { webhookService } from "@/libs/ws/webhookService";
export default {
  mixins: [ValidMixin],
  props: {
    service: Object,
    dialog: Boolean,
    editItem: Object,
  },
  data() {
    return {
      loading: false,
      show_secret: false,
      show_webhook_key: false,
      saved_webhook_url: "",
      saved_webhook_key: "",
      saved_webhook_auth_header: "",
      saved_webhook_auth_bearer: true,
      api_doc_url: "",
      event_types: Object.entries(webhookService.EVENT_TYPE_NAMES).map(([id, name]) => ({ id, name })),
      item: {
        id: "",
        name: "",
        api_id: "",
        api_secret: "",
        webhook_url: "",
        webhook_key: "",
        webhook_events: [],
        webhook_auth_header: "",
        webhook_auth_bearer: true,
      },
    };
  },
  watch: {
    dialog() {
      if (this.dialog) {
        this.show_secret = false;
        this.show_webhook_key = false;

        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }

        if (this.editItem != null && this.editItem.id > 0) {
          this.item = {
            id: this.editItem.id,
            name: this.editItem.name,
            api_id: this.editItem.user ? this.editItem.user.api_id : "",
            api_secret: this.editItem.user ? this.editItem.user.api_secret : "",
            webhook_url: this.editItem.user ? this.editItem.user.webhook_url : "",
            webhook_key: this.editItem.user ? this.editItem.user.webhook_key : "",
            webhook_events: this.parseWebhookEvents(this.editItem.user ? this.editItem.user.webhook_events : null),
            // NULL = Authorization con Bearer (comportamiento por defecto)
            webhook_auth_header: (this.editItem.user && this.editItem.user.webhook_auth_header) || "Authorization",
            webhook_auth_bearer: this.editItem.user && this.editItem.user.webhook_auth_bearer != null ? !!this.editItem.user.webhook_auth_bearer : true,
          };
          this.saved_webhook_url = this.item.webhook_url;
          this.saved_webhook_key = this.item.webhook_key;
          this.saved_webhook_auth_header = this.item.webhook_auth_header;
          this.saved_webhook_auth_bearer = this.item.webhook_auth_bearer;
        }
      }
    },
  },
  computed: {
    has_credentials() {
      return !!this.item.api_id;
    },
    has_unsaved_webhook_changes() {
      return (
        this.item.webhook_url !== this.saved_webhook_url ||
        (this.item.webhook_key || "") !== (this.saved_webhook_key || "") ||
        (this.item.webhook_auth_header || "") !== (this.saved_webhook_auth_header || "") ||
        this.item.webhook_auth_bearer !== this.saved_webhook_auth_bearer
      );
    },
    webhook_auth_preview() {
      return (this.item.webhook_auth_header || "Authorization") + ": " + (this.item.webhook_auth_bearer ? "Bearer " : "") + "<token>";
    },
    api_base_url() {
      const api_url = process.env.VUE_APP_API_URL || "";
      const app_url = api_url.trim().replace(/\/api\/?$/, "/");
      return app_url + "external/v1/";
    },
  },
  created() {
    this.rules.url = (value) => /^https?:\/\/.+/.test(value) || value == "" || value == null || "La URL debe comenzar con http:// o https://";
    this.rules.header_name = (value) => !value || (value.length <= 100 && /^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/.test(value)) || "Nombre de header inválido (sin espacios ni caracteres especiales)";
    this.rules.webhook_key = (value) => !value || value.length <= 255 || "El token no puede superar los 255 caracteres";
    this.loadApiDocUrl();
  },
  methods: {
    parseWebhookEvents(webhook_events) {
      // NULL = suscripto a todos los eventos
      if (webhook_events == null) return this.event_types.map((event_type) => event_type.id);
      if (Array.isArray(webhook_events)) return [...webhook_events];
      try {
        return JSON.parse(webhook_events) || [];
      } catch (e) {
        return [];
      }
    },
    loadApiDocUrl() {
      generalService
        .request("api-doc-url")
        .then((response) => {
          if (response?.status && response?.result?.url && response.result.url !== "-") {
            this.api_doc_url = response.result.url;
          }
        })
        .catch(() => {});
    },
    copy(value) {
      if (!value) return;
      navigator.clipboard.writeText(value);
      this.toast.success("Copiado al portapapeles.");
    },
    onSubmit() {
      if (this.valid) this.doSave(false);
    },
    generate() {
      this.doSave(true);
    },
    doSave(regenerate) {
      if (this.loading) return;
      this.loading = true;

      const data = {
        id: this.item.id,
        webhook_url: this.item.webhook_url,
        webhook_key: this.item.webhook_url ? this.item.webhook_key : null,
        webhook_auth_header: this.item.webhook_auth_header,
        webhook_auth_bearer: this.item.webhook_auth_bearer,
        regenerate: regenerate,
        // Si estan todos tildados se guarda NULL (todos), asi los eventos nuevos quedan suscriptos
        webhook_events: this.event_types.every((event_type) => this.item.webhook_events.includes(event_type.id)) ? null : this.item.webhook_events,
      };

      this.service
        .saveApiCredentials(data)
        .then((response) => {
          if (response.status) {
            this.item.api_id = response.result.api_id;
            this.item.api_secret = response.result.api_secret;
            this.item.webhook_url = response.result.webhook_url;
            this.saved_webhook_url = response.result.webhook_url;
            this.item.webhook_key = response.result.webhook_key;
            this.saved_webhook_key = response.result.webhook_key;
            this.item.webhook_auth_header = response.result.webhook_auth_header || "Authorization";
            this.saved_webhook_auth_header = this.item.webhook_auth_header;
            this.item.webhook_auth_bearer = response.result.webhook_auth_bearer;
            this.saved_webhook_auth_bearer = this.item.webhook_auth_bearer;
            this.item.webhook_events = this.parseWebhookEvents(response.result.webhook_events);
            this.toast.success("Credenciales guardadas correctamente.");
            this.$emit("loadList");
          } else {
            if (Object.prototype.hasOwnProperty.call(response, "message")) {
              this.toast.error(response.message);
            } else {
              this.toast.error(response.msg);
            }
          }
        })
        .catch((e) => {
          this.toast.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    testWebhook() {
      if (this.loading) return;
      this.loading = true;

      this.service
        .testWebhook({ id: this.item.id })
        .then((response) => {
          if (response.status) {
            this.toast.success(response.msg || "Webhook de prueba enviado correctamente.");
          } else {
            let error_msg = Object.prototype.hasOwnProperty.call(response, "message") ? response.message : response.msg;
            if (response.result && response.result.last_response_log) {
              error_msg += " - " + response.result.last_response_log;
              this.toast.error(error_msg, { autoClose: 15000 });
            } else {
              this.toast.error(error_msg);
            }
          }
        })
        .catch((e) => {
          this.toast.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
/* Grisa el campo y bloquea foco/edición solo sobre el input,
   dejando interactivos los íconos del slot append (copiar/mostrar). */
.locked-field :deep(.v-field) {
  pointer-events: none;
  opacity: 0.7;
}
</style>
